import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { executeCapability } from '../utils/capabilityExecute'
import { useAuth } from './AuthContext'
import {
  promptForWebPushIfDefault,
  refreshWebPushSubscriptionIfGranted,
} from '../utils/webPushSubscribe'

export interface Notification {
  _id: string
  user: string
  type: 'system' | 'info' | 'warning' | 'error' | 'success' | 'form' | 'safety' | 'equipment' | 'project' | 'leave'
  title: string
  message: string
  date: string
  resolved: boolean
  requiresAction: boolean
  dismissible: boolean
  priority: 'low' | 'medium' | 'high'
  read?: boolean
  alertKey?: string
  url?: string
}

interface NotificationContextType {
  notifications: Notification[]
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  dismissNotification: (notificationId: string) => Promise<void>
  dismissAllNotifications: () => Promise<void>
  refreshNotifications: () => Promise<void>
  unreadCount: number
  loading: boolean
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

const POLL_MS = 60_000
const MARK_READ_PARAM = 'markNotificationRead'

function consumeMarkReadFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const id = url.searchParams.get(MARK_READ_PARAM)
    if (!id) return null
    url.searchParams.delete(MARK_READ_PARAM)
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    return id
  } catch {
    return null
  }
}

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setNotifications([])
      setLoading(false)
      return
    }
    try {
      const list = await executeCapability<Notification[]>({
        app: 'global',
        capability: 'notifications.list',
        input: {},
      })
      setNotifications(Array.isArray(list) ? list : [])
    } catch {
      // Keep existing list on transient failures
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, user])

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!notificationId) return
    setNotifications((prev) =>
      prev.map((note) => (note._id === notificationId ? { ...note, read: true } : note))
    )
    try {
      await executeCapability({
        app: 'global',
        capability: 'notifications.markAsRead',
        input: { notificationId },
      })
    } catch {
      void refreshNotifications()
    }
  }, [refreshNotifications])

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setNotifications([])
      setLoading(false)
      return
    }
    setLoading(true)
    void refreshNotifications()
    if (!user.isKiosk) {
      void refreshWebPushSubscriptionIfGranted()
      void promptForWebPushIfDefault(user._id)
    }

    const pendingMarkRead = consumeMarkReadFromUrl()
    if (pendingMarkRead) {
      void markAsRead(pendingMarkRead)
    }

    const onFocus = () => {
      if (document.visibilityState === 'visible') void refreshNotifications()
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    const timer = window.setInterval(() => void refreshNotifications(), POLL_MS)

    const onSwMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || data.type !== 'MARK_NOTIFICATION_READ') return
      const id = typeof data.notificationId === 'string' ? data.notificationId : ''
      if (id) void markAsRead(id)
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage)
    }

    return () => {
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
      window.clearInterval(timer)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage)
      }
    }
  }, [isAuthenticated, user, refreshNotifications, markAsRead])

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((note) => (note.read ? note : { ...note, read: true })))
    try {
      await executeCapability({
        app: 'global',
        capability: 'notifications.markAllAsRead',
        input: {},
      })
    } catch {
      void refreshNotifications()
    }
  }, [refreshNotifications])

  const dismissNotification = useCallback(async (notificationId: string) => {
    setNotifications((prev) => prev.filter((note) => note._id !== notificationId))
    try {
      await executeCapability({
        app: 'global',
        capability: 'notifications.dismiss',
        input: { notificationId },
      })
    } catch {
      void refreshNotifications()
    }
  }, [refreshNotifications])

  const dismissAllNotifications = useCallback(async () => {
    setNotifications((prev) => prev.filter((note) => note.dismissible === false))
    try {
      await executeCapability({
        app: 'global',
        capability: 'notifications.dismissAll',
        input: {},
      })
    } catch {
      void refreshNotifications()
    }
  }, [refreshNotifications])

  const unreadCount = notifications.filter((note) => !note.read).length

  const value: NotificationContextType = {
    notifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    dismissAllNotifications,
    refreshNotifications,
    unreadCount,
    loading,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
